import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import type { AnalyticsRefereeRow } from '@proref/types';
import { pct, timeAgo } from '../../lib/format';

interface RefereeRosterTableProps {
  referees: AnalyticsRefereeRow[];
  windowDays: number;
  minRankedAttempts: number;
}

// Columns an admin actually scans. `numeric` right-aligns and switches the default sort direction:
// clicking a name should start A→Z, but clicking a score should start with the highest.
type SortKey = 'full_name' | 'clips_answered' | 'accuracy' | 'completion_rate' | 'exam_average' | 'current_streak' | 'last_active_at';

function columns(t: TFunction): { key: SortKey; label: string; numeric?: boolean; hint?: (days: number) => string }[] {
  return [
    { key: 'full_name', label: t('admin.analytics.roster.colReferee') },
    { key: 'clips_answered', label: t('admin.analytics.roster.colAnswered'), numeric: true },
    { key: 'accuracy', label: t('admin.analytics.roster.colAccuracy'), numeric: true },
    {
      key: 'completion_rate',
      label: t('admin.analytics.roster.colDailyLoop'),
      numeric: true,
      hint: (d) => t('admin.analytics.roster.dailyLoopHint', { days: d }),
    },
    {
      key: 'exam_average',
      label: t('admin.analytics.roster.colExams'),
      numeric: true,
      hint: () => t('admin.analytics.roster.examsHint'),
    },
    { key: 'current_streak', label: t('admin.analytics.roster.colStreak'), numeric: true },
    { key: 'last_active_at', label: t('admin.analytics.roster.colLastActive'), numeric: true },
  ];
}

export function RefereeRosterTable({ referees, windowDays, minRankedAttempts }: RefereeRosterTableProps) {
  const { t } = useTranslation();
  const COLUMNS = columns(t);
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'full_name', desc: false });

  const rows = useMemo(() => sortReferees(referees, sort), [referees, sort]);

  function toggle(key: SortKey, numeric: boolean) {
    // Re-clicking the active column flips it; a new column starts in its natural direction —
    // descending for scores (best first), ascending for names.
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: numeric }));
  }

  return (
    <div className="overflow-x-auto rounded border border-line bg-surface shadow-1">
      {/* The scroller was already here, but `w-full` alone gives it nothing to scroll: the table
          shrinks to the container and crushes seven columns into a phone's width instead. The
          min-width is what makes the overflow real — name plus six metric columns. */}
      <table className="w-full min-w-[720px] border-collapse text-[13px]">
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                title={col.hint?.(windowDays)}
                className={`border-b border-line px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted ${
                  col.numeric ? 'text-right' : 'text-left'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggle(col.key, Boolean(col.numeric))}
                  className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-ink"
                  aria-sort={sort.key === col.key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
                >
                  {col.label}
                  {sort.key === col.key && <span aria-hidden>{sort.desc ? '↓' : '↑'}</span>}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-line last:border-0 hover:bg-surface-2">
              <td className="px-3 py-2.5">
                <Link to={`/admin/analytics/referees/${r.id}`} className="block hover:underline">
                  <div className="font-medium text-ink">{r.full_name ?? r.email}</div>
                  <div className="text-[11px] text-muted">
                    {r.licence_level ?? t('admin.analytics.roster.noLicence')} · {r.email}
                  </div>
                </Link>
              </td>
              <td className="px-3 py-2.5 text-right font-mono">{r.clips_answered}</td>
              <td className="px-3 py-2.5 text-right">
                <AccuracyCell row={r} minRankedAttempts={minRankedAttempts} />
              </td>
              <td className="px-3 py-2.5 text-right font-mono">{pct(r.completion_rate)}</td>
              <td className="px-3 py-2.5 text-right font-mono">{pct(r.exam_average)}</td>
              <td className="px-3 py-2.5 text-right font-mono">{r.current_streak}</td>
              <td className="px-3 py-2.5 text-right text-muted">{timeAgo(r.last_active_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Accuracy plus its trend arrow. An unranked referee still shows their rate — an admin wants to see
// the newcomer — but it's muted and carries the reason, so it doesn't read as comparable to the
// established officials either side of it.
function AccuracyCell({ row, minRankedAttempts }: { row: AnalyticsRefereeRow; minRankedAttempts: number }) {
  const { t } = useTranslation();
  if (row.accuracy == null) return <span className="text-muted-2">—</span>;

  const delta = row.accuracy_delta;
  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      {delta != null && Math.abs(delta) >= 0.01 && (
        <span className={`text-[11px] ${delta > 0 ? 'text-success' : 'text-danger'}`}>
          {delta > 0 ? '▲' : '▼'}
          {Math.abs(Math.round(delta * 100))}
        </span>
      )}
      <span
        className={`font-mono font-semibold ${row.ranked ? 'text-ink' : 'text-muted'}`}
        title={row.ranked ? undefined : t('admin.analytics.roster.needsToRank', { count: row.clips_answered, min: minRankedAttempts })}
      >
        {pct(row.accuracy)}
        {!row.ranked && <span className="ml-0.5 text-[10px]">*</span>}
      </span>
    </span>
  );
}

// Sorting rules that the naive comparator gets wrong:
//   • null is "unknown", not "worst" — it sinks to the bottom in BOTH directions, so flipping the
//     sort never parades referees with no data across the top.
//   • unranked referees sort below ranked ones on accuracy specifically. A newcomer at 1-for-1
//     would otherwise top the table at 100%, which is the exact misreading `ranked` exists to stop.
function sortReferees(rows: AnalyticsRefereeRow[], sort: { key: SortKey; desc: boolean }): AnalyticsRefereeRow[] {
  const dir = sort.desc ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (sort.key === 'accuracy' && a.ranked !== b.ranked) return a.ranked ? -1 : 1;

    const av = value(a, sort.key);
    const bv = value(b, sort.key);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
    return ((av as number) - (bv as number)) * dir;
  });
}

function value(row: AnalyticsRefereeRow, key: SortKey): string | number | null {
  if (key === 'full_name') return row.full_name ?? row.email;
  if (key === 'last_active_at') return row.last_active_at ? new Date(row.last_active_at).getTime() : null;
  return row[key];
}
